import { deleteHttp } from "../../../common/api/apiShared.ts";
import { createElement, useState } from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { Trans } from "react-i18next";
import { PaymentTermViewModel } from "./PaymentTerms.types.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export default function usePaymentTermsHooks() {
    const url = "/paymentTerms";
    const { isLoading, data: contractTypes, refetch } = useDotLegalQuery<Array<PaymentTermViewModel>>(url);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const { translateString } = useTranslation();
    const [paymentTermForDeletion, setPaymentTermForDeletion] = useState<PaymentTermViewModel | undefined>(undefined);
    const [paymentTermForUpdate, setPaymentTermForUpdate] = useState<PaymentTermViewModel | undefined>();
    const snackBar = useDotLegalSnackbar();

    const { permissions } = useUserContext();

    const deletePaymentTerm = async (id: string) => {
        return await deleteHttp(`/paymentTerms/${id}`);
    };

    const deleteMutation = useDotLegalMutation(deletePaymentTerm, {
        onSuccess: async (result) => {
            if (result.success()) {
                await refetch();
                snackBar.show(createElement(Trans, { i18nKey: "itemDeletedSuccess", values: { name: paymentTermForDeletion?.name } }));
            } else {
                snackBar.show(createElement(Trans, { i18nKey: "cantDeleteContractType", values: { name: paymentTermForDeletion?.name } }), "warning");
            }
            setPaymentTermForDeletion(undefined);
        },
    });

    const onCreateClick = () => {
        setShowCreateDialog(true);
    };

    const onSaved = async () => {
        await refetch();
        onDialogClose();
    };

    const onDialogClose = () => {
        setPaymentTermForUpdate(undefined);
        setShowCreateDialog(false);
    };

    const onDelete = () => {
        deleteMutation.mutate(paymentTermForDeletion!.id);
    };

    const getMenuItems = (contractType: PaymentTermViewModel) => {
        let menuItems = [];
        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => {
                    setPaymentTermForUpdate(contractType);
                },
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => {
                    setPaymentTermForDeletion(contractType);
                },
            });
        }

        return menuItems;
    };

    return {
        isLoading,
        contractTypes,
        getMenuItems,
        canManageMasterData: permissions.canManageMasterData,
        showCreateDialog,
        onSaved,
        paymentTermForDeletion,
        paymentTermForUpdate,
        setPaymentTermForDeletion,
        onDelete,
        onCreateClick,
        onDialogClose,
        isDeleting: deleteMutation.isLoading,
    };
}
